// Категория местности объекта (C05 экспертизы 03.11.2026): по ней сверка
// расценок и расчёт по тендеру берут колонку норматива НР (приказ 812/пр) —
// обычную, для приравненных к Крайнему Северу местностей или для районов
// Крайнего Севера. Категория не указана — НР по обычной ставке, как раньше.
//
// Чистая логика без React — проверяется node:test (territoryCategory.test.mjs).
import type { TerritoryCategory } from '@shared/api';

export const TERRITORY_OPTIONS: ReadonlyArray<{ value: TerritoryCategory; label: string }> = [
  { value: 'normal', label: 'обычные условия' },
  { value: 'mprks', label: 'местности, приравненные к районам Крайнего Севера' },
  { value: 'rks', label: 'районы Крайнего Севера' },
];

/** Подпись категории; не указана — словами, а не пустым полем. */
export function territoryLabel(value: string | null | undefined): string {
  return TERRITORY_OPTIONS.find((o) => o.value === value)?.label ?? 'не указана';
}

/** Значение из выпадающего списка: пустое — категория не указана. */
export function parseTerritory(raw: string): TerritoryCategory | null {
  return TERRITORY_OPTIONS.find((o) => o.value === raw)?.value ?? null;
}

/** Подсказка у выбора категории при создании проверки или расчёта. */
export const TERRITORY_HINT =
  'Категория местности определяет ставку накладных расходов. Не указана — ставка обычная; ' +
  'для районов Крайнего Севера и приравненных местностей укажите категорию.';

/** Категория результата для пометки: без категории — словами. */
function resultCategoryText(value: string | null | undefined): string {
  return value ? `«${territoryLabel(value)}»` : 'без категории (ставка обычная)';
}

/**
 * Пометка «результат посчитан по другой категории» («Аудит 40», Z1).
 * `result` — категория, по которой посчитан показанный результат; `undefined`
 * — результата нет, сравнивать не с чем. Пометка выводится из данных сервера,
 * а не из того, что меняли на этой странице: она переживает конец расчёта и
 * переоткрытие карточки и гаснет только после пересчёта с текущей категорией.
 */
export function territoryStaleNote(
  current: string | null | undefined,
  result: string | null | undefined,
  rerunLabel: string,
): string | null {
  if (result === undefined) return null;
  if ((current ?? null) === (result ?? null)) return null;
  const now = current ? `«${territoryLabel(current)}»` : 'не указана';
  return (
    `Результат посчитан для категории ${resultCategoryText(result)}, сейчас выбрана ${now}. ` +
    `Нажмите «${rerunLabel}», чтобы пересчитать накладные расходы.`
  );
}

/** Состояние поля категории — по карточке, для которой оно возникло. */
export interface TerritoryFieldState {
  /** Карточка (проверка или расчёт), к которой относятся `saving` и `error`. */
  forId: string;
  saving: boolean;
  error: string | null;
}

/** Что показать на карточке `id`: состояние другой карточки — не показывать. */
export function fieldStateFor(state: TerritoryFieldState | null, id: string): TerritoryFieldState {
  return state && state.forId === id ? state : { forId: id, saving: false, error: null };
}

/** Начато сохранение категории на карточке `id`. */
export function saveStarted(id: string): TerritoryFieldState {
  return { forId: id, saving: true, error: null };
}

/**
 * Пришёл ответ сохранения для карточки `forId`. Пока он шёл, могли открыть
 * другую карточку и начать там своё — тогда поздний ответ её состояние не
 * трогает.
 */
export function saveFinished(
  state: TerritoryFieldState | null,
  forId: string,
  error: string | null,
): TerritoryFieldState | null {
  if (state && state.forId !== forId) return state;
  return { forId, saving: false, error };
}
